import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  semanticGovernanceApi,
  type BusinessContext,
  type BusinessTerm,
  type CanonicalEntity,
  type DimensionDefinition,
  type KPIDefinition,
  type KnowledgeGraph,
  type LineageRecord,
  type MetadataEntry,
  type SemanticDashboard,
  type SemanticHealth,
} from '../../api/semantic';

type Tab = 'overview' | 'glossary' | 'entities' | 'kpis' | 'dimensions' | 'graph' | 'lineage' | 'metadata' | 'context' | 'health';
type Theme = 'light' | 'dark';

export function SemanticGovernancePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SemanticDashboard | null>(null);
  const [terms, setTerms] = useState<BusinessTerm[]>([]);
  const [entities, setEntities] = useState<CanonicalEntity[]>([]);
  const [kpis, setKpis] = useState<KPIDefinition[]>([]);
  const [dimensions, setDimensions] = useState<DimensionDefinition[]>([]);
  const [graph, setGraph] = useState<KnowledgeGraph | null>(null);
  const [lineage, setLineage] = useState<LineageRecord[]>([]);
  const [metadata, setMetadata] = useState<MetadataEntry[]>([]);
  const [health, setHealth] = useState<SemanticHealth | null>(null);
  const [context, setContext] = useState<BusinessContext | null>(null);
  const [kindFilter, setKindFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, glossary, entList, kpiList, dims, g, lin, meta, hlth] = await Promise.all([
        semanticGovernanceApi.dashboard(),
        semanticGovernanceApi.listGlossary(),
        semanticGovernanceApi.listEntities(),
        semanticGovernanceApi.listKPIs(),
        semanticGovernanceApi.listDimensions(),
        semanticGovernanceApi.knowledgeGraph(),
        semanticGovernanceApi.listLineage(),
        semanticGovernanceApi.listMetadata(),
        semanticGovernanceApi.health(),
      ]);
      setDashboard(dash);
      setTerms(glossary);
      setEntities(entList);
      setKpis(kpiList);
      setDimensions(dims);
      setGraph(g);
      setLineage(lin);
      setMetadata(meta);
      setHealth(hlth);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load semantic layer');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loadContext = async () => {
    setBusy(true);
    try {
      setContext(await semanticGovernanceApi.businessContext({ include_policy: true, include_planning: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load business context');
    } finally {
      setBusy(false);
    }
  };

  const kinds = [...new Set(entities.map((e) => e.kind))];
  const filteredEntities = kindFilter ? entities.filter((e) => e.kind === kindFilter) : entities;
  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'glossary', label: 'Business Glossary' },
    { id: 'entities', label: 'Canonical Entities' },
    { id: 'kpis', label: 'KPI Registry' },
    { id: 'dimensions', label: 'Dimensions' },
    { id: 'graph', label: 'Knowledge Graph' },
    { id: 'lineage', label: 'Lineage Explorer' },
    { id: 'metadata', label: 'Metadata Catalog' },
    { id: 'context', label: 'Business Context' },
    { id: 'health', label: 'Semantic Health' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">DEV-000082</p>
          <h1 className="text-2xl font-semibold">Enterprise Semantic Layer</h1>
          <p className={`text-sm ${muted}`}>Canonical business model, glossary, KPI registry, knowledge graph & AI context</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/persistence-governance" className="text-indigo-500 hover:underline">Persistence Platform</Link>
            <span className={muted}>·</span>
            <Link to="/platform/api-gateway" className="text-indigo-500 hover:underline">API Gateway</Link>
            <span className={muted}>·</span>
            <Link to="/knowledge" className="text-indigo-500 hover:underline">Knowledge Hub</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
          <button type="button" onClick={refresh} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">Refresh</button>
        </div>
      </div>

      {error && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-indigo-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && health && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Semantic Health Score</p><p className="mt-1 text-3xl font-semibold text-indigo-500">{health.semantic_health_score.toFixed(1)}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Canonical Entities</p><p className="mt-1 text-3xl font-semibold">{dashboard.entities}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Glossary Terms</p><p className="mt-1 text-3xl font-semibold">{dashboard.glossary_terms}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>KPI Definitions</p><p className="mt-1 text-3xl font-semibold">{dashboard.kpis}</p></div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="font-medium">Extension Points</h2>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {dashboard.extension_points.map((ep) => (
                <li key={ep.dev_id}><span className="font-mono text-indigo-500">{ep.dev_id}</span> — {ep.name}: {ep.description}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'glossary' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {terms.map((t) => (
            <div key={t.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{t.term}</h3>
                <span className={`text-xs ${t.status === 'approved' ? 'text-emerald-500' : 'text-amber-500'}`}>{t.status}</span>
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{t.business_definition}</p>
              <p className={`mt-1 text-xs ${muted}`}>Owner: {t.business_owner}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'entities' && (
        <div className="space-y-4">
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Filter by kind">
            <option value="">All kinds</option>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <div className="overflow-x-auto rounded-xl border">
            <table className={`min-w-full text-sm ${card}`}>
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Display</th>
                  <th className="px-4 py-2 text-left">Kind</th>
                  <th className="px-4 py-2 text-left">Family</th>
                  <th className="px-4 py-2 text-left">Reused By</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntities.map((e) => (
                  <tr key={e.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                    <td className="px-4 py-2 font-mono text-xs">{e.name}</td>
                    <td className="px-4 py-2">{e.display_name}</td>
                    <td className="px-4 py-2">{e.kind}</td>
                    <td className="px-4 py-2">{e.product_family ?? '—'}</td>
                    <td className="px-4 py-2 text-xs">{(e.reused_by ?? []).slice(0, 3).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'kpis' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 text-left">Identifier</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Formula</th>
                <th className="px-4 py-2 text-left">Owner</th>
                <th className="px-4 py-2 text-left">Units</th>
              </tr>
            </thead>
            <tbody>
              {kpis.map((k) => (
                <tr key={k.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2 font-mono text-xs">{k.identifier}</td>
                  <td className="px-4 py-2">{k.business_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{k.formula}</td>
                  <td className="px-4 py-2">{k.owner}</td>
                  <td className="px-4 py-2">{k.units}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'dimensions' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {dimensions.map((d) => (
            <div key={d.id} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">{d.display_name}</h3>
              <p className={`text-xs font-mono ${muted}`}>{d.name}</p>
              <p className={`mt-2 text-sm ${muted}`}>{d.description}</p>
              {d.hierarchy && <p className={`mt-1 text-xs ${muted}`}>Hierarchy: {d.hierarchy.join(' → ')}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'graph' && graph && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Nodes</p><p className="mt-1 text-2xl font-semibold">{graph.nodes.length}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Edges</p><p className="mt-1 text-2xl font-semibold">{graph.edges.length}</p></div>
          </div>
          <div className="overflow-x-auto rounded-xl border max-h-96 overflow-y-auto">
            <table className={`min-w-full text-sm ${card}`}>
              <thead className={`sticky top-0 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                <tr><th className="px-4 py-2 text-left">From</th><th className="px-4 py-2 text-left">Relation</th><th className="px-4 py-2 text-left">To</th></tr>
              </thead>
              <tbody>
                {graph.edges.map((e) => {
                  const from = graph.nodes.find((n) => n.id === e.from_id);
                  const to = graph.nodes.find((n) => n.id === e.to_id);
                  return (
                    <tr key={e.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                      <td className="px-4 py-2">{from?.label ?? e.from_id}</td>
                      <td className="px-4 py-2 text-indigo-500">{e.relation}</td>
                      <td className="px-4 py-2">{to?.label ?? e.to_id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'lineage' && (
        <div className="space-y-4">
          {lineage.map((l) => (
            <div key={l.id} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-mono text-sm font-medium">{l.metric_or_kpi}</h3>
              <p className={`text-sm ${muted}`}>Source: {l.source_system} → {l.calculation ?? l.transformation}</p>
              <p className={`mt-1 text-xs ${muted}`}>Consumers: {(l.consumers ?? []).join(', ') || '—'}</p>
              {l.ai_agents && <p className={`text-xs ${muted}`}>AI Agents: {l.ai_agents.join(', ')}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'metadata' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 text-left">Kind</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Source</th>
                <th className="px-4 py-2 text-left">Completeness</th>
              </tr>
            </thead>
            <tbody>
              {metadata.map((m) => (
                <tr key={m.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2">{m.kind}</td>
                  <td className="px-4 py-2">{m.name}</td>
                  <td className="px-4 py-2">{m.source}</td>
                  <td className="px-4 py-2">{m.completeness_pct.toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'context' && (
        <div className="space-y-4">
          <button type="button" onClick={loadContext} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50">Load AI Business Context</button>
          {context && (
            <div className={`rounded-xl border p-4 ${card} space-y-4`}>
              {context.entity_summaries && context.entity_summaries.length > 0 && (
                <div><h3 className="font-medium">Entities</h3><ul className={`mt-1 text-sm ${muted}`}>{context.entity_summaries.map((e) => <li key={e.name}>{e.name}: {e.description}</li>)}</ul></div>
              )}
              {context.kpi_context && context.kpi_context.length > 0 && (
                <div><h3 className="font-medium">KPI Context</h3><ul className={`mt-1 text-sm ${muted}`}>{context.kpi_context.map((k) => <li key={k.identifier}>{k.name} — {k.formula}</li>)}</ul></div>
              )}
              {context.planning_context && context.planning_context.length > 0 && (
                <div><h3 className="font-medium">Planning Context</h3><ul className={`mt-1 text-sm ${muted}`}>{context.planning_context.map((p) => <li key={p.plan_type}>{p.plan_type} ({p.horizon}): {p.description}</li>)}</ul></div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'health' && health && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Glossary Coverage</p><p className="mt-1 text-2xl font-semibold">{health.glossary_coverage_pct.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Entity Reuse Rate</p><p className="mt-1 text-2xl font-semibold">{health.entity_reuse_rate_pct.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Metadata Completeness</p><p className="mt-1 text-2xl font-semibold">{health.metadata_completeness_pct.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Lineage Completeness</p><p className="mt-1 text-2xl font-semibold">{health.lineage_completeness_pct.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Semantic Adoption</p><p className="mt-1 text-2xl font-semibold">{health.semantic_adoption_pct.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Context Usage (24h)</p><p className="mt-1 text-2xl font-semibold">{health.context_usage_24h}</p></div>
        </div>
      )}
    </div>
  );
}
